"use client";

import { useMemo, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { useFacets } from "@/hooks/use-facets";
import type { BrowserState } from "@/lib/search/url-state";
import type { FilterSelection } from "@/lib/search/parser";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Chip } from "@/components/ui/chip";
import { Segmented } from "@/components/ui/segmented";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type FilterField = FilterSelection["field"];

const FILTER_META: Record<FilterField, { label: string; singular: string; searchable: boolean }> = {
  categories: { label: "Categories", singular: "category", searchable: false },
  traits: { label: "Traits", singular: "trait", searchable: true },
  keywords: { label: "Keywords", singular: "keyword", searchable: true },
  author: { label: "Authors", singular: "author", searchable: true },
};

const FIELD_ORDER: FilterField[] = ["categories", "traits", "keywords", "author"];

export type FacetCounts = Record<string, { value: string; count: number }[]> | undefined;

interface FiltersPanelProps {
  state: BrowserState;
  onChange: (next: Partial<BrowserState>) => void;
  /** Facet counts from the current result set, when available. */
  facetCounts?: FacetCounts;
  className?: string;
  /** Hide the built-in "Filters" heading (when a sheet supplies its own). */
  hideHeader?: boolean;
  /** "sheet" lets short lists expand fully to avoid nested scrolling on touch. */
  variant?: "rail" | "sheet";
}

export function FiltersPanel({
  state,
  onChange,
  facetCounts,
  className,
  hideHeader = false,
  variant = "rail",
}: FiltersPanelProps) {
  const { data: facets, isLoading } = useFacets();

  const allValues = useMemo(
    () => ({
      author: facets?.authors ?? [],
      categories: facets?.categories ?? [],
      traits: facets?.traits ?? [],
      keywords: facets?.keywords ?? [],
    }),
    [facets]
  );

  const totalSelected = state.filters.reduce((n, f) => n + f.values.length, 0);

  return (
    <div className={cn("flex flex-col", className)}>
      {!hideHeader && (
        <div className="flex items-center justify-between pb-3">
          <h2 className="font-display text-[13px] font-semibold tracking-[0.02em] text-ink">
            Filters
            {totalSelected > 0 && (
              <span className="ml-2 rounded-full bg-accent px-1.5 py-px font-mono text-[10.5px] text-void">
                {totalSelected}
              </span>
            )}
          </h2>
          {totalSelected > 0 && (
            <button
              type="button"
              onClick={() => onChange({ filters: [] })}
              className="text-[12px] font-medium text-ink-3 transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-accent"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="divide-y divide-line">
          {FIELD_ORDER.map((field) => (
            <FilterGroup
              key={field}
              field={field}
              values={allValues[field]}
              counts={facetCounts?.[field]}
              selection={state.filters.find((f) => f.field === field)}
              defaultOpen={field === "categories" || field === "traits"}
              variant={variant}
              onChange={(selection) => {
                const next = state.filters.filter((f) => f.field !== field);
                if (selection && selection.values.length > 0) next.push(selection);
                onChange({ filters: next });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface FilterGroupProps {
  field: FilterField;
  values: string[];
  counts?: { value: string; count: number }[];
  selection?: FilterSelection;
  defaultOpen: boolean;
  variant: "rail" | "sheet";
  onChange: (selection?: FilterSelection) => void;
}

function FilterGroup({
  field,
  values,
  counts,
  selection,
  defaultOpen,
  variant,
  onChange,
}: FilterGroupProps) {
  const meta = FILTER_META[field];
  const selected = useMemo(() => selection?.values ?? [], [selection]);
  const operator = selection?.operator ?? "or";
  const [open, setOpen] = useState(defaultOpen || selected.length > 0);

  const countMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of counts ?? []) map.set(c.value, c.count);
    return map;
  }, [counts]);

  // Selected values float to the top, then everything else in facet order.
  const ordered = useMemo(() => {
    const selectedSet = new Set(selected);
    return [
      ...selected.filter((v) => values.includes(v)),
      ...values.filter((v) => !selectedSet.has(v)),
    ];
  }, [values, selected]);

  const toggle = (value: string) => {
    const nextValues = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(nextValues.length > 0 ? { field, values: nextValues, operator } : undefined);
  };

  const panelId = `filter-${field}`;

  return (
    <section className="py-2.5">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-md py-1.5 text-left focus-visible:outline-2 focus-visible:outline-accent"
      >
        <span className="flex items-center gap-2">
          <span className="text-[13.5px] font-medium text-ink">{meta.label}</span>
          {selected.length > 0 && (
            <span className="rounded-full bg-accent-tint px-1.5 py-px font-mono text-[10.5px] text-accent">
              {selected.length}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn(
            "size-4 text-ink-3 transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>

      {open && (
        <div id={panelId} className="mt-1.5 space-y-2 animate-fade-in">
          {selected.length > 1 && (
            <div className="flex items-center justify-between gap-2 rounded-md bg-surface-1 px-2 py-1.5">
              <span className="text-[12px] text-ink-3">Match</span>
              <Segmented
                size="xs"
                aria-label={`${meta.label} matching`}
                value={operator}
                onChange={(op) => onChange({ field, values: selected, operator: op })}
                options={[
                  {
                    value: "or",
                    label: "any",
                    title: `Essences with any selected ${meta.singular}`,
                  },
                  {
                    value: "and",
                    label: "all",
                    title: `Essences with every selected ${meta.singular}`,
                  },
                ]}
              />
            </div>
          )}

          <Command
            className="bg-transparent"
            filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
          >
            {meta.searchable && (
              <CommandInput
                placeholder={`Find ${meta.singular}…`}
                aria-label={`Search ${meta.label.toLowerCase()}`}
              />
            )}
            <CommandList
              className={cn(
                "mt-1",
                variant === "sheet"
                  ? meta.searchable
                    ? "max-h-72"
                    : "max-h-none"
                  : meta.searchable
                    ? "max-h-56"
                    : "max-h-72"
              )}
            >
              <CommandEmpty>No matching {meta.singular}.</CommandEmpty>
              {ordered.map((value) => {
                const isSelected = selected.includes(value);
                const count = countMap.get(value);
                return (
                  <CommandItem
                    key={value}
                    value={value}
                    onSelect={() => toggle(value)}
                    aria-selected={isSelected}
                    className={cn("gap-2.5 py-1.5", isSelected && "text-ink")}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "flex size-4 shrink-0 items-center justify-center rounded-[5px] border transition-colors",
                        isSelected
                          ? "border-accent-strong bg-accent-strong text-white"
                          : "border-line-strong bg-surface-1"
                      )}
                    >
                      {isSelected && <Check className="size-3" strokeWidth={3} />}
                    </span>
                    <span className="min-w-0 flex-1 truncate" title={value}>
                      {value}
                    </span>
                    {count !== undefined && (
                      <span className="ml-auto shrink-0 font-mono text-[11px] tabular text-ink-4">
                        {count}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandList>
          </Command>
        </div>
      )}

      {!open && selected.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {selected.map((v) => (
            <Chip key={v} onRemove={() => toggle(v)} removeLabel={`Remove ${v}`}>
              {v}
            </Chip>
          ))}
        </div>
      )}
    </section>
  );
}
